import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnInit,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { ComponentStore } from '@ngrx/component-store';
import { combineLatest, filter, map, Observable, switchMap, take, withLatestFrom } from 'rxjs';

import { FormBuilderComponent } from '@blaze-dashboard/form-builder-feature';
import { RuntimeFacade } from '@blaze-runtime/engine-feature';
import { DatasetInfo } from '@blaze-shared/api-contracts-domain';
import {
  ConnectorsApiService,
  ConnectorsCollectionService,
  ConnectorsTemplatesCollectionService,
} from '@blaze-shared/connectors-data-access';
import {
  type ConnectorConfiguration,
  type ConnectorTemplate,
  ConnectorTestResult,
  type FormValues,
} from '@blaze-shared/connectors-domain';
import {
  LocalStorageService,
  NotificationService,
  StorageKey,
} from '@blaze-shared/platform-data-access';
import { compareAsc, compareAscBy } from '@blaze-shared/platform-util';
import {
  CopyConnectorConfigEnvButtonComponent,
  EnvironmentIndicatorComponent,
} from '@blaze-shared/shared-feature';
import {
  ButtonBarComponent,
  ElementLoadingIndicatorComponent,
  PanelComponent,
  PanelRef,
} from '@blaze-shared/shared-ui';

import { DataConnectorsStore } from '../+state/data-connectors.store';
import { prepareEditedConnectorConfiguration } from '../utils';

@Component({
  selector: 'blaze-data-connector-details',
  imports: [
    AsyncPipe,
    ButtonBarComponent,
    CopyConnectorConfigEnvButtonComponent,
    ElementLoadingIndicatorComponent,
    EnvironmentIndicatorComponent,
    FormBuilderComponent,
    FormsModule,
    MatIconModule,
    PanelComponent,
    ReactiveFormsModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ComponentStore],
  host: { class: 'data-connector-dialog' },
  templateUrl: './data-connector-details.component.html',
})
export class DataConnectorDetailsComponent implements OnInit {
  private readonly connectorsApiService = inject(ConnectorsApiService);
  private readonly connectorsCollService = inject(ConnectorsCollectionService);
  private readonly connTplCollService = inject(ConnectorsTemplatesCollectionService);
  private readonly dataConnectorsStore = inject(DataConnectorsStore);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly notificationService = inject(NotificationService);
  private readonly panelRef = inject(PanelRef);
  private readonly runtimeFacade = inject(RuntimeFacade);

  showNext = output<void>();

  @Input() connector?: ConnectorConfiguration;
  @Input() initialFormValues?: FormValues;
  @Input() template?: ConnectorTemplate;

  @ViewChild('formBuilder') formBuilderRef!: FormBuilderComponent;

  formConfig$ = this.dataConnectorsStore.formConfig$;
  coupledConnector$ = this.dataConnectorsStore.coupledConnector$;
  isConnectionValid$: Observable<boolean>;

  datasets$: Observable<DatasetInfo[]>;
  actions: string[] = [];
  loadingForm = signal(true);
  formValid = false;

  get createOrUpdateButtonLabel(): string {
    return this.connector?.id ? 'update connector' : 'create connector';
  }

  constructor() {
    this.datasets$ = this.dataConnectorsStore.availableDatasets$;
    this.isConnectionValid$ = this.isConnectionValid();
  }

  ngOnInit(): void {
    const templateId = this.template?.id || this.connector?.templateId;
    this.connTplCollService.getByKey(templateId).subscribe((template) => {
      this.dataConnectorsStore.setFormConfig(template?.configuration?.form || []);
      this.loadingForm.set(false);
    });
  }

  closeDialog(): void {
    this.panelRef.close();
  }

  createOrUpdateConnector(): void {
    // A set of connectors that are scoped to the project instead of account.
    const projectScopedConnectors = new Set<string>(['Foxy', 'NMI']);
    const connectorId = this.connector?.id;

    combineLatest({
      formValues: this.dataConnectorsStore.formValues$,
      projectId: this.runtimeFacade.loadedProjectId$,
    })
      .pipe(
        take(1),
        map(({ formValues, projectId }) => {
          const config: ConnectorConfiguration = {
            accountId: this.localStorageService.get(StorageKey.AccountId) || '',
            actions: [],
            configuration: { ...formValues },
            datasets: [],
            name: formValues?.['name'] as string,
            templateId: this.template?.id || this.connector?.templateId,
            projectId:
              this.template?.target && projectScopedConnectors.has(this.template?.target)
                ? projectId
                : undefined,
          };
          return config;
        }),
        switchMap((connectorConfig) =>
          connectorId
            ? this.connectorsCollService.update(connectorConfig, { correlationId: connectorId })
            : this.connectorsCollService.add(connectorConfig),
        ),
        filter((response) => !!response?.id),
      )
      .subscribe(() => {
        this.closeDialog();
      });
  }

  copyConfiguration(connector: ConnectorConfiguration): void {
    this.formBuilderRef.fillFormWithValues(connector.configuration);
  }

  isCreateOrUpdate(): boolean {
    return !!this.template;
  }

  isTestableConnection(): boolean {
    if (this.template?.connectorType === 'Api' || this.connector?.connectorType === 'Api') {
      return false;
    }
    return true;
  }

  testConnector(): void {
    if (this.connector) {
      this.testExistingConnector();
    } else {
      this.testNewConnector();
    }
  }

  updateFormValid(formValid: boolean): void {
    this.formValid = formValid;
  }

  updateFormValues(formData: FormValues): void {
    this.dataConnectorsStore.setFormValues(formData);
    this.dataConnectorsStore.deleteAvailableDatasets();
  }

  private testNewConnector(): void {
    this.dataConnectorsStore.formValues$
      .pipe(
        take(1),
        map((formValues) => {
          return {
            accountId: this.localStorageService.get(StorageKey.AccountId),
            configuration: formValues,
            templateId: this.template?.id || '',
          } as ConnectorTestResult;
        }),
        switchMap((connectorTestData) => {
          return this.connectorsApiService.postTest(connectorTestData);
        }),
      )
      .subscribe({
        next: (available) => {
          const datasets = available?.datasets?.toSorted(compareAscBy('name')) || [];
          const apiActions = available?.actions?.toSorted(compareAscBy('name')) || [];
          this.dataConnectorsStore.setAvailableApiActions(apiActions);
          this.dataConnectorsStore.setAvailableDatasets(datasets);
        },
        error: (error) => {
          this.notificationService.showError(error);
        },
      });
  }

  private testExistingConnector(): void {
    this.dataConnectorsStore.formValues$
      .pipe(
        take(1),
        withLatestFrom(this.formConfig$),
        map(([formValues, formConfig]) => {
          return prepareEditedConnectorConfiguration(
            formConfig,
            formValues,
            this.connector?.datasets,
          );
        }),
        switchMap((request) => {
          const connectorId = this.connector?.id || '';
          return this.connectorsApiService.postTestEditedConnector(connectorId, request);
        }),
      )
      .subscribe({
        next: (testResult) => {
          const availableDatasets = testResult.availableDatasets?.toSorted(compareAsc) || [];
          const previouslySelectedDatasets =
            testResult.previouslySelectedDatasets?.toSorted(compareAsc) || [];
          this.dataConnectorsStore.setAvailableDatasets(availableDatasets);
          this.dataConnectorsStore.setPreviouslySelectedDatasets(previouslySelectedDatasets);
          this.dataConnectorsStore.setConnectorConfigurationChanged(testResult.hasChanges);
        },
        error: (error) => {
          this.notificationService.showError(error);
        },
      });
  }

  /**
   * Only when we have a list of available datasets the next button should be enabled.
   * @returns
   */
  private isConnectionValid(): Observable<boolean> {
    return this.dataConnectorsStore.availableDatasets$.pipe(
      map((datasets) => datasets?.length > 0),
    );
  }
}
